import { CircularProgress, Drawer, Grid, Typography } from '@mui/material'
import {
  DrawerContent,
  DrawerHeader,
  DrawerStickyFooter,
  DrawerTitle,
} from 'components/mobile/Drawer'
import { themeOptions } from 'components/modals/SupportModal'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  BaseField,
  CloseIcon,
  PlusIcon,
  ReactSelect,
  StyledButton,
  StyledDialog,
  StyledTypography,
  UPLOAD_IMAGE_TYPES,
  createUppy,
} from 'shared'
import { API } from 'api'
import { DashboardModal } from '@uppy/react'
import { useModal, withModal } from 'utils/providers/ModalProvider'

import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import { sendSupportLetterAPI } from 'api/support'
import { supportValidationSchema, SupportPayload } from 'utils/validations'

import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useAuth } from 'utils/providers/AuthProvider'
import { yupResolver } from '@hookform/resolvers/yup'

const SupportDrawerContent = ({
  setOpen,
}: {
  open: boolean
  setOpen: (open: boolean) => void
}) => {
  const { showModal } = useModal()
  const [uppyOpened, setUppyOpened] = useState(false)
  const { user } = useAuth()

  const supportMutation = useMutation({
    mutationFn: sendSupportLetterAPI,
  })

  const { control, handleSubmit, setValue } = useForm<SupportPayload>({
    resolver: yupResolver(supportValidationSchema),
    defaultValues: {
      email: '',
      topic: !user?.profile?.isVerified
        ? themeOptions[themeOptions.length - 1]
        : themeOptions[0],
      question: '',
      attachmentUrl: '',
    },
  })

  const [uppy] = useState(() => {
    const uppy = createUppy(API)

    uppy.setOptions({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: UPLOAD_IMAGE_TYPES,
        maxNumberOfFiles: 1,
      },
    })

    uppy.on('complete', (data) => {
      const [file] = data.successful

      setUppyOpened(false)
      uppy.removeFile(file.id)
      setValue('attachmentUrl', decodeURIComponent(file.uploadURL))
    })

    return uppy
  })

  const submit = handleSubmit(
    async (data) => {
      try {
        await supportMutation.mutateAsync({
          ...data,
          topic: data.topic.label,
        })
        setOpen(false)
        showModal('supportSuccess')
      } catch (err) {
        console.error(err)
      }
    },
    (err) => {
      toast.error('Something went wrong. Please try again later')
      console.log(err)
    }
  )

  useEffect(() => {
    if (!user) return
    setValue('email', user.email)
  }, [user, setValue])

  return (
    <DrawerContent>
      <DrawerHeader position="static" zIndex="0">
        <DrawerTitle fontSize="24px" fontWeight="700">
          Hi Beauty. <br />
          How can we help?
        </DrawerTitle>
        <CloseIcon onClick={() => setOpen(false)} />
      </DrawerHeader>

      <Typography mb={2}>
        Write your question here, we will respond to your email as soon as
        possible
      </Typography>
      <Grid container flexDirection="column" gap={1.5}>
        {user?.email ? null : (
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => {
              return (
                <BaseField
                  value={field.value}
                  label="Email address"
                  error={fieldState.invalid}
                  placeholder="Type email address here"
                  helperText={fieldState.error?.message}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )
            }}
          />
        )}
        {user?.profile?.isVerified ? (
          <Controller
            control={control}
            name="topic"
            render={({ field, fieldState }) => {
              return (
                <ReactSelect
                  name=""
                  options={themeOptions}
                  value={field.value}
                  label="Select topic"
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  placeholder="Select theme here"
                />
              )
            }}
          />
        ) : null}
        <Controller
          control={control}
          name="question"
          render={({ field, fieldState }) => {
            return (
              <BaseField
                name=""
                value={field.value}
                label="Question"
                error={fieldState.invalid}
                placeholder="Type your question here"
                helperText={fieldState.error?.message}
                onChange={(e) => field.onChange(e.target.value)}
                multiline
                rows={2}
              />
            )
          }}
        />

        {user?.email ? (
          <>
            <Controller
              control={control}
              name="attachmentUrl"
              render={({ field }) => {
                if (field.value) {
                  return (
                    <Grid
                      width="fit-content"
                      position="relative"
                      onClick={() => {
                        if (!field.value) return
                        showModal('mediaPreview', { url: field.value })
                      }}
                    >
                      <img
                        style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '16px',
                          objectFit: 'cover',
                        }}
                        src={field.value}
                        alt=""
                      />
                      <Grid
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          top: -8,
                          right: -8,
                          padding: '4px',
                          position: 'absolute',
                          borderRadius: '100%',
                          backgroundColor: ({ palette }) =>
                            palette.common.white,
                        }}
                      >
                        <CloseIcon
                          onClick={(e) => {
                            e.stopPropagation()
                            field.onChange('')
                          }}
                          sx={{
                            width: '16px',
                            height: '16px',
                          }}
                        />
                      </Grid>
                    </Grid>
                  )
                }
                return (
                  <Grid>
                    <Typography>Attach file</Typography>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="center"
                      onClick={() => setUppyOpened(true)}
                      sx={{
                        height: '92px',
                        width: '100%',
                        borderRadius: '8px',
                        border: '1px dashed rgba(3, 16, 29, 0.50)',
                      }}
                    >
                      <PlusIcon />
                    </Grid>
                  </Grid>
                )
              }}
            />
            <DashboardModal
              open={uppyOpened}
              onRequestClose={() => setUppyOpened(false)}
              uppy={uppy}
              proudlyDisplayPoweredByUppy={false}
            />
          </>
        ) : null}
      </Grid>
      <DrawerStickyFooter>
        <StyledButton
          fullWidth
          onClick={submit}
          variant="contained"
          startIcon={
            supportMutation.isLoading ? (
              <CircularProgress size="1em" color="inherit" />
            ) : null
          }
          disabled={supportMutation.isLoading}
        >
          Submit
        </StyledButton>
      </DrawerStickyFooter>
    </DrawerContent>
  )
}

export const SupportDrawer = ({
  open,
  setOpen,
}: {
  open: boolean
  setOpen: (open: boolean) => void
}) => {
  return (
    <Drawer
      open={open}
      anchor="bottom"
      onClose={() => setOpen(false)}
      PaperProps={{ sx: { borderRadius: '16px 16px 0px 0px' } }}
    >
      <SupportDrawerContent open={open} setOpen={setOpen} />
    </Drawer>
  )
}

export const SupportSuccessModal = withModal('supportSuccess')((props) => {
  const { closeModal, isOpen } = props
  return (
    <StyledDialog open={isOpen} onClose={closeModal}>
      <Grid container flexWrap="nowrap" mb={3}>
        <Typography
          textAlign="center"
          mx="auto"
          fontSize="24px"
          fontWeight="700"
          lineHeight="1"
        >
          Your message <br /> has been received
        </Typography>
        <CloseIcon onClick={closeModal} />
      </Grid>
      <StyledTypography mb={3} textAlign="center">
        The Hue team will get back to you <br /> by email soon.
      </StyledTypography>

      <StyledButton onClick={closeModal} fullWidth variant="contained">
        Back to home
      </StyledButton>
    </StyledDialog>
  )
})
